import { signInWithRedirect } from "aws-amplify/auth";

/**
 * The answer on the wire (GET /auth/sso-discovery): the Cognito provider name
 * of the single sign-on the address's domain uses, or null. Snake case,
 * because this call does not go through the Api slice (services/apis/api.ts),
 * whose base query camelizes every response: that query fetches a Cognito
 * access token for each request and throws without one, and discovery is
 * asked before anyone has signed in.
 */
type SsoDiscoveryAnswer = { provider_name: string | null };

/**
 * The provider to send the browser to, or null when the address's domain
 * uses no single sign-on here.
 *
 * The one thing the sign-in screen asks the server between its email step
 * and its credential step. It is a fact about the domain, never about the
 * address: management-api reads the domain's verified owner and that
 * organization's connection, and answers the same for every address on it,
 * so typing one reveals no account (specs/saml-scim/discovery-slice.md).
 * Whether the organization enforces its single sign-on yet is not part of
 * the answer: a hit is a redirect either way.
 *
 * Rejects with a message for the person when the answer could not be had --
 * a throttled network, an outage -- rather than guessing "no": a member of an
 * organization that enforces single sign-on has no other way in, and a guess
 * would send them to a credential step that cannot work.
 */
export async function discoverSingleSignOn(
    email: string,
): Promise<string | null> {
    const base = (
        (import.meta.env.VITE_MANAGEMENT_API_URL as string | undefined) ?? ""
    ).replace(/\/$/, "");
    const url = `${base}/auth/sso-discovery?email=${encodeURIComponent(email.trim())}`;

    let response: Response;
    try {
        response = await fetch(url, {
            headers: { accept: "application/json" },
        });
    } catch {
        throw new SsoDiscoveryFailed();
    }

    if (!response.ok) {
        // A 429 carries a sentence written for the reader; anything else is
        // ours to word.
        const body = (await response.json().catch(() => null)) as {
            message?: unknown;
        } | null;
        throw new SsoDiscoveryFailed(
            response.status === 429 && typeof body?.message === "string"
                ? body.message
                : undefined,
        );
    }

    const answer = (await response.json()) as SsoDiscoveryAnswer;

    return answer.provider_name || null;
}

/** Discovery had no answer; the message is for the person waiting on it. */
export class SsoDiscoveryFailed extends Error {
    constructor(
        message = "We couldn't check how to sign you in. Please try again.",
    ) {
        super(message);
        this.name = "SsoDiscoveryFailed";
    }
}

/**
 * Send the browser to the organization's identity provider, through Cognito:
 * `identity_provider=<name>` on the pool's authorize endpoint, which skips
 * Cognito's own screens and redirects straight to the IdP. The name is the
 * one management-api's connection row carries, exactly as the pool has it.
 *
 * What happens on the way back is `SessionStarter`'s: a member's account is
 * re-pointed at the profile Cognito made, an invitee joins, or the sign-in is
 * refused (specs/saml-scim/identity-swap.md). Nothing here sets the sign-up
 * attribution cookie: a sign-in through single sign-on never starts a trial.
 */
export function continueWithSingleSignOn(providerName: string): Promise<void> {
    return signInWithRedirect({ provider: { custom: providerName } });
}
